import {FC, useMemo} from 'react'
import {Overlay, Popover, Form, Button} from 'react-bootstrap'
import {Tag} from '../../../../../services/cellcast.type'
import {ContactStatus} from '../../../../../services/cellcast.v2.types'
import './Filter.scss'
import Select from 'react-select'

interface FilterPopoverProps {
  show: boolean
  target: HTMLElement | null
  columnType: string
  onClose: () => void
  onApply: () => void
  onReset: () => void

  // Controlled inputs
  includeTag: Tag[]
  excludeTag: Tag[]
  containValue: string
  statusValue: ContactStatus | undefined
  tagOptions: Tag[]
  onIncludeTagChange: (val: Tag[]) => void
  onExcludeTagChange: (val: Tag[]) => void
  onContainValueChange: (val: string) => void
  onStatusValueChange: (val: ContactStatus) => void
}

const FilterPopover: FC<FilterPopoverProps> = ({
  show,
  target,
  columnType,
  onClose,
  onApply,
  onReset,

  includeTag,
  excludeTag,
  containValue,
  statusValue,
  tagOptions,
  onIncludeTagChange,
  onExcludeTagChange,
  onContainValueChange,
  onStatusValueChange,
}) => {
  const isTagColumn = columnType?.toLowerCase() === 'tags' || columnType?.toLowerCase() === 'list'
  const isStatusColumn = columnType?.toLowerCase() === 'status'

  const filteredTagOptions = useMemo(() => {
    let filteredOptions = tagOptions
    if (columnType?.toLowerCase() === 'list') {
      filteredOptions = tagOptions.filter((tag) => tag.type === 'list')
    }
    return filteredOptions
  }, [tagOptions])

  const isDisabled = useMemo(() => {
    if (isTagColumn) {
      return includeTag.length === 0 && excludeTag.length === 0
    } else if (isStatusColumn) {
      return !statusValue
    } else {
      return !containValue
    }
  }, [isTagColumn, isStatusColumn, includeTag, excludeTag, statusValue, containValue])

  return (
    <Overlay show={show} target={target} placement='bottom' rootClose onHide={onClose}>
      {({placement, arrowProps, show: _show, popper, ...props}) => (
        (
          <Popover id='filter-popover' {...props}>
            {/* Popover Header */}
            <Popover.Header
              className='d-flex justify-content-between align-items-center text-primary'
              style={{backgroundColor: '#F3F0FB'}}
            >
              <strong className='text-capitalize'>{columnType}</strong>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={onClose}
              ></button>
            </Popover.Header>

            {/* Popover Body */}
            <Popover.Body>
              {isTagColumn ? (
                <>
                  {/* Tag column UI */}
                  <Form.Group className='mb-3'>
                    <Form.Label className='form-label'>Include Tag</Form.Label>
                    <Select
                      isMulti
                      isClearable={false}
                      aria-label='Select include tag'
                      placeholder='Select include Tag'
                      options={filteredTagOptions.map(tag => ({
                        value: tag._id,
                        label: tag.name,
                      }))}
                      value={includeTag.map(tag => ({
                        value: tag._id,
                        label: tag.name,
                      }))}
                      onChange={(selected) => {
                        const selectedTags = (selected as { value: string; label: string }[]).map(opt =>
                          filteredTagOptions.find(tag => tag._id === opt.value)!
                        )
                        onIncludeTagChange(selectedTags)
                      }}
                    />
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>Exclude Tag</Form.Label>
                     <Select
                      isMulti
                      isClearable={false}
                      aria-label='Select exclude tag'
                      placeholder='Select exclude Tag'
                      options={filteredTagOptions.map(tag => ({
                        value: tag._id,
                        label: tag.name,
                      }))}
                      value={excludeTag.map(tag => ({
                        value: tag._id,
                        label: tag.name,
                      }))}
                      onChange={(selected) => {
                        const selectedTags = (selected as { value: string; label: string }[]).map(opt =>
                          filteredTagOptions.find(tag => tag._id === opt.value)!
                        )
                        onExcludeTagChange(selectedTags)
                      }}
                    />
                  </Form.Group>
                </>
              ) : isStatusColumn ? (
                <>
                  {/* Status column UI */}
                  <Form.Group className='mb-3'>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      aria-label='Select status'
                      value={statusValue}
                      onChange={(e) => onStatusValueChange(e.target.value as ContactStatus)}
                    >
                      <option value=''>Select status</option>
                      <option value={ContactStatus.subscribe}>Subscribe</option>
                      <option value={ContactStatus.unsubscribe}>Unsubscribe</option>
                    </Form.Select>
                  </Form.Group>
                </>
              ) : (
                <>
                  {/* Non-tag and non-status column UI */}
                  <Form.Group className='mb-3'>
                    <Form.Label>Contain</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Input keyword'
                      value={containValue}
                      onChange={(e) => onContainValueChange(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}

              {/* Footer Buttons */}
              <div className='d-flex justify-content-end'>
                <Button
                  variant='outline-primary'
                  className='me-2 btn-outline btn-outline-primary !important'
                  onClick={() => {
                    onReset()
                    onClose()
                  }}
                >
                  Reset
                </Button>
                <Button variant='primary' onClick={onApply} disabled={isDisabled}>
                  Apply
                </Button>
              </div>
            </Popover.Body>
          </Popover>
        )
      )}
    </Overlay>
  )
}

export default FilterPopover
